import React, { useEffect, useMemo, useState } from 'react';
import Button from '../common/Button.js';
import ProductDialog from './ProductDialog.js';
import Pagination from '../common/Pagination.js';
import useAppStore from '../../store/useAppStore.js';
import {
  createProduct,
  createProductCategory,
  deleteProduct,
  fetchInventory,
  fetchProductCategories,
  fetchProducts,
  updateProduct
} from '../../services/inventoryApi.js';

const Products = () => {
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9); // 3x3 grid

  const {
    products,
    setProducts,
    productSearchName,
    setProductSearchName,
    productSearchCategory,
    setProductSearchCategory,
    getFilteredProducts,
    productCategories,
    addProductCategory,
    removeProductCategory,
    setProductCategories,
    setProductsLoaded,
    productHasLoaded,
    productLastFetched,
    inventoryItems,
    initializeInventory,
    inventoryHasLoaded,
    initializeProducts,
    branchReceiptData
  } = useAppStore();

  const loadCategories = async () => {
    try {
      const response = await fetchProductCategories();
      const categories = response.categories || [];
      setProductCategories(categories.map((category) => ({ label: category.name, value: category.name })));
    } catch (error) {
      console.error('Failed to load product categories:', error);
    }
  };

  // Load product, inventory, and category data once on mount
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        initializeInventory(fetchInventory),
        initializeProducts(fetchProducts),
        loadCategories()
      ]);
      setProductsLoaded(true);
      setFeedback('');
    } catch (error) {
      setFeedback(error.message || 'Unable to load product inventory data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openProductDialog = (product = null) => {
    setSelectedProduct(product);
    setShowProductDialog(true);
    setFeedback('');
  };

  const closeProductDialog = () => {
    setSelectedProduct(null);
    setShowProductDialog(false);
  };

  const handleSaveProduct = async (payload) => {
    setLoading(true);
    try {
      if (selectedProduct) {
        const response = await updateProduct(selectedProduct.id, payload);
        const savedProduct = response.product || response;
        // Update store instead of reloading all data
        setProducts(products.map(p => p.id === selectedProduct.id ? savedProduct : p));
        setFeedback('Product updated successfully.');
      } else {
        const response = await createProduct(payload);
        const savedProduct = response.product || response;
        // Add to store instead of reloading all data
        setProducts([savedProduct, ...products]);
        setFeedback('Product created successfully.');
      }
      closeProductDialog();
    } catch (error) {
      setFeedback(error.message || 'Unable to save product.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    setLoading(true);
    try {
      await deleteProduct(id);
      // Remove from store instead of reloading all data
      setProducts(products.filter(p => p.id !== id));
      setFeedback('Product deleted successfully.');
      if (selectedProduct?.id === id) {
        closeProductDialog();
      }
    } catch (error) {
      setFeedback(error.message || 'Unable to delete product.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const trimmedCategory = newCategory.trim();
    if (!trimmedCategory) {
      setFeedback('Enter a category name.');
      return;
    }
    if (productCategories.some((cat) => cat.value.toLowerCase() === trimmedCategory.toLowerCase())) {
      setFeedback('This category already exists.');
      return;
    }

    setLoading(true);
    try {
      const response = await createProductCategory(trimmedCategory);
      const created = response.category;
      if (created) {
        addProductCategory(created.name);
        setNewCategory('');
        setFeedback('Category added successfully.');
      } else {
        setFeedback('Unable to save category.');
      }
    } catch (error) {
      setFeedback(error.message || 'Unable to save category.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCategory = (value) => {
    const itemsUsingCategory = products.filter((product) => product.category === value);
    if (itemsUsingCategory.length > 0) {
      setFeedback(`Cannot remove category "${value}" - it's used by ${itemsUsingCategory.length} product(s).`);
      return;
    }
    removeProductCategory(value);
    setFeedback('Category removed successfully.');
  };

  const inventoryById = useMemo(
    () => inventoryItems.reduce((map, item) => ({ ...map, [item.id]: item }), {}),
    [inventoryItems]
  );

  const getAvailabilityStatus = (product) => {
    const shortages = [];
    const recipeItems = product.recipe || product.recipeDetails || [];
    recipeItems.forEach((recipeItem) => {
      const inventory = inventoryById[recipeItem.inventoryId];
      if (!inventory) {
        shortages.push(`${recipeItem.name || 'Ingredient'} missing`);
      } else if (inventory.stock <= 0) {
        shortages.push(`${inventory.name} out of stock`);
      } else if (inventory.stock < Number(recipeItem.quantity || 0)) {
        shortages.push(`${inventory.name} insufficient`);
      }
    });
    return {
      available: shortages.length === 0,
      statusText: shortages.length > 0 ? shortages.join(', ') : 'Ready to sell'
    };
  };

  const filteredProducts = useMemo(() => {
    const state = { 
      products, 
      productSearchName, 
      productSearchCategory,
      getFilteredProducts 
    };
    return getFilteredProducts(state);
  }, [products, productSearchName, productSearchCategory, getFilteredProducts]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [productSearchName, productSearchCategory]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredProducts.slice(startIndex, startIndex + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const insights = useMemo(() => {
    const totalInventoryValue = inventoryItems.reduce((sum, item) => sum + item.stock * item.purchasePrice, 0);
    const limitedProducts = products.filter((product) => !getAvailabilityStatus(product).available).length;
    return {
      totalInventoryValue: totalInventoryValue.toFixed(2),
      lowStockProducts: limitedProducts,
      productCount: products.length,
      ingredientCount: inventoryItems.length
    };
  }, [inventoryItems, products, inventoryById, products.length]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Product Management</h1>
          <p className="text-gray-600 mt-2">Create products with ingredient recipes, track cost, and manage stock consumption.</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Products</p>
            <p className="text-2xl font-semibold">{insights.productCount}</p>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Inventory items</p>
            <p className="text-2xl font-semibold">{insights.ingredientCount}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-md">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold">Product Catalog</h2>
              <p className="text-gray-500">Review your product recipes, margins, and availability status.</p>
            </div>
            <div className="flex gap-3">
              <div className="rounded-2xl bg-orange-50 px-4 py-3 text-sm text-orange-700">
                {insights.lowStockProducts} product(s) limited by current stock
              </div>
              <Button variant="primary" onClick={() => openProductDialog()}>
                Add Product
              </Button>
            </div>
          </div>

          {feedback && <div className="mb-4 rounded-2xl bg-blue-50 p-3 text-sm text-blue-700">{feedback}</div>}

          {/* Search and Filter Section */}
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Search by Name</label>
              <input
                type="text"
                placeholder="Search product name..."
                value={productSearchName}
                onChange={(e) => setProductSearchName(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Filter by Category</label>
              <select
                value={productSearchCategory}
                onChange={(e) => setProductSearchCategory(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Categories</option>
                {productCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category Management */}
          <div className="mb-6 rounded-3xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold">Manage Categories</h3>
                <p className="text-gray-500 text-sm">Create and remove product categories.</p>
              </div>
              <Button variant="primary" onClick={() => setShowCategoryDialog(!showCategoryDialog)}>
                {showCategoryDialog ? 'Hide' : 'Manage'}
              </Button>
            </div>

            {showCategoryDialog && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="New category name..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="flex-1 rounded-2xl border border-gray-300 px-4 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <Button variant="primary" onClick={handleAddCategory}>Add</Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {productCategories.map((cat) => (
                    <div key={cat.value} className="rounded-full bg-white border border-gray-300 px-4 py-2 flex items-center gap-2 text-sm">
                      <span>{cat.label}</span>
                      <button
                        onClick={() => handleRemoveCategory(cat.value)}
                        className="text-red-600 hover:text-red-700 font-semibold"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-gray-600">Loading products...</div>
          ) : (
            <div>
              <p className="mb-4 text-sm text-gray-600">
                Showing {paginatedProducts.length} of {filteredProducts.length} products (Total: {products.length})
              </p>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {paginatedProducts.map((product) => {
                  const availability = getAvailabilityStatus(product);
                  return (
                    <div key={product.id} className="rounded-3xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-col h-full">
                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold">{product.name}</h3>
                          <p className="text-sm text-gray-500 mb-4">{product.category}</p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center mt-auto">
                          <div className='flex gap-2 pt-4'>
                            <Button variant="secondary" onClick={() => openProductDialog(product)}>
                              Edit
                            </Button>
                            <Button variant="danger" onClick={() => handleDeleteProduct(product.id)}>
                              Delete
                            </Button>
                          </div>
                          <div className="rounded-xl p-3 text-lg text-gray-700">PKR {(product.price || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredProducts.length}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}

              {filteredProducts.length === 0 && (
                <div className="rounded-2xl bg-gray-50 p-6 text-center text-gray-600">
                  No products found matching your search criteria.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ProductDialog
        isOpen={showProductDialog}
        onClose={closeProductDialog}
        onSave={handleSaveProduct}
        product={selectedProduct}
        inventoryItems={inventoryItems}
        categoryOptions={productCategories}
        tokenPapersCount={branchReceiptData?.tokenPapers || 1}
        loading={loading}
      />
    </div>
  );
};

export default Products;
